/**
 * Looking inside a project's memory: browse what is in it, open an item to see what it says, where
 * it came from and what it connects to, change its label with a reason, share it with the
 * organisation, or remove it for good. Owners also decide whether the project's memory is mirrored
 * into the organisation's, and write the labelling rules as plain rows rather than a document.
 */

import { hasManyTeams, SENSITIVITIES, teamOfTags, teamWord, type LabellingRule, type MemoryItem, type MemoryItemKind, type Sensitivity } from '@aioe/shared';
import { Database } from '@gravity-ui/icons';
import { Segment } from '@heroui-pro/react';
import { useMemo, useState } from 'react';

import { routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { SubjectName } from './SubjectPicker.tsx';
import { Button, ChoiceSelect, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, StatusMark, Tick } from './ui.tsx';
import { VIEW_TONES } from './ui/Art.tsx';
import { RowMenu } from './ui/RowMenu.tsx';
import { Markdown } from './Markdown.tsx';

const SENSITIVITY_TEXT: Record<Sensitivity, string> = { public: 'Public', internal: 'Internal', confidential: 'Confidential', restricted: 'Restricted' };
const KIND_WORD: Record<MemoryItemKind, string> = { node: 'Concept', edge: 'Relation', community: 'Group', note: 'Note', lesson: 'Lesson', decision: 'Decision', artifact: 'Artifact', knowledge: 'Knowledge' };
const VIEWS = {
  written: { label: 'Notes and decisions', kinds: ['note', 'lesson', 'decision'] as MemoryItemKind[] },
  concepts: { label: 'Concepts and groups', kinds: ['node', 'community', 'artifact'] as MemoryItemKind[] },
} as const;
type View = keyof typeof VIEWS;
const SHOWN = 200;

/** The team a decision came from (its `team:` tag, D239). */
const teamOf = (i: MemoryItem) => (i.kind === 'decision' ? teamOfTags((i.data as { tags?: unknown[] }).tags) : undefined);

const nameOf = (i: MemoryItem) => {
  const d = i.data as Record<string, unknown>;
  return (typeof d.title === 'string' && d.title) || (typeof d.name === 'string' && d.name) || i.id;
};

export function MemoryBrowser({ projectId, owner }: { projectId: string; owner: boolean }) {
  const tone = VIEW_TONES.projects!;
  const [view, setView] = useState<View>('written');
  const [filter, setFilter] = useState('');
  const [open, setOpen] = useState<string>();
  const list = useLoad(() => routes.memoryGraph(projectId, VIEWS[view].kinds), [projectId, view]);
  const items = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return [...(list.data?.items ?? [])].reverse().filter((i) => !f || nameOf(i).toLowerCase().includes(f) || i.rootId.toLowerCase().includes(f) || (teamWord(teamOf(i)) ?? '').toLowerCase().includes(f));
  }, [list.data, filter]);
  // A team is named only when the decisions come from more than one (D239).
  const manyTeams = useMemo(() => hasManyTeams((list.data?.items ?? []).map((i) => teamOf(i)?.id)), [list.data]);
  return (
    <ListGroup
      title="What is in it"
      count={list.data ? items.length : undefined}
      description="Newest first. Open one to see what it says, where it came from and what it connects to."
      actions={
        <Segment aria-label="Which items" size="sm" selectedKey={view} onSelectionChange={(k) => (setView(String(k) as View), setOpen(undefined))} className="w-fit">
          {(Object.keys(VIEWS) as View[]).map((k) => (
            <Segment.Item key={k} id={k}>
              {VIEWS[k].label}
            </Segment.Item>
          ))}
        </Segment>
      }
    >
      <ListBlock>
        <input className={inputClass} value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by name, repository or team" aria-label="Filter" />
      </ListBlock>
      {list.error ? (
        <ListBlock>
          <ErrorNote error={list.error} />
        </ListBlock>
      ) : null}
      {!list.data && !list.error ? <ListBlock className="text-sm text-muted">Reading the memory…</ListBlock> : null}
      {list.data && items.length === 0 ? (
        <ListBlock>
          {!filter && list.data.itemsFiltered > 0 ? (
            <Empty tone={tone} icon={<Database />} compact title="Nothing here you can read" body={`${list.data.itemsFiltered} ${list.data.itemsFiltered === 1 ? 'item is' : 'items are'} hidden from you by their labels. The note at the top of this tab says why.`} />
          ) : (
            <Empty tone={tone} icon={<Database />} compact title={filter ? 'Nothing matches' : view === 'written' ? 'No notes or decisions yet' : 'No concepts yet'} body={view === 'written' ? 'Agents write notes, lessons and decisions back from their sessions; they show here.' : 'A member’s workbench sends what it reads from each repository.'} />
          )}
        </ListBlock>
      ) : null}
      {items.slice(0, SHOWN).flatMap((i) => [
        <ListRow
          key={i.id}
          title={nameOf(i)}
          description={`${KIND_WORD[i.kind]} · ${i.rootId || 'the project'} · ${SENSITIVITY_TEXT[i.label.sensitivity]}${i.label.compartments.length ? `, ${i.label.compartments.join(', ')}` : ''} · ${timeAgo(i.updatedAt)}${manyTeams && teamWord(teamOf(i)) ? ` · ${teamWord(teamOf(i))}` : ''}`}
          trailing={open === i.id ? <span className="text-xs text-muted">Open</span> : undefined}
          onPress={() => setOpen(open === i.id ? undefined : i.id)}
        />,
        open === i.id ? <ItemDetail key={`${i.id}/detail`} projectId={projectId} item={i} owner={owner} onChange={() => (setOpen(undefined), list.reload())} /> : null,
      ])}
      {items.length > SHOWN ? <ListBlock className="text-xs text-muted">The newest {SHOWN} of {items.length}. Filter to narrow it.</ListBlock> : null}
    </ListGroup>
  );
}

/**
 * One item opened from elsewhere (the explorer's Open): the same detail as a row of the list opens, at the top of Browse,
 * whatever its kind, with a way to put it away.
 */
export function OpenedMemoryItem({ projectId, itemId, owner, onClose }: { projectId: string; itemId: string; owner: boolean; onClose: () => void }) {
  const found = useLoad(() => routes.memoryItemsBatch(projectId, [itemId]).then((r) => r.items[0] ?? null), [projectId, itemId]);
  const i = found.data;
  return (
    <ListGroup
      title={i ? nameOf(i) : 'Opened item'}
      description={
        i ? (
          <>
            {KIND_WORD[i.kind]} · {i.rootId || 'the project'} · {SENSITIVITY_TEXT[i.label.sensitivity]}
            {i.label.compartments.length ? `, ${i.label.compartments.join(', ')}` : ''} · changed {timeAgo(i.updatedAt)}
          </>
        ) : undefined
      }
      actions={
        <Button size="sm" variant="ghost" onPress={onClose}>
          Close
        </Button>
      }
    >
      {found.error ? (
        <ListBlock>
          <ErrorNote error={found.error} />
        </ListBlock>
      ) : null}
      {i === null ? <ListBlock className="text-sm text-muted">That item is no longer there, or you may no longer read it.</ListBlock> : i ? <ItemDetail projectId={projectId} item={i} owner={owner} onChange={found.reload} /> : !found.error ? <ListBlock className="text-sm text-muted">Opening the item…</ListBlock> : null}
    </ListGroup>
  );
}

function ItemDetail({ projectId, item, owner, onChange }: { projectId: string; item: MemoryItem; owner: boolean; onChange: () => void }) {
  const detail = useLoad(() => routes.memoryExplain(projectId, item.id), [projectId, item.id]);
  const [mode, setMode] = useState<'label' | 'remove'>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string>();
  const d = detail.data;
  const run = async (work: () => Promise<unknown>, after: string) => {
    setBusy(true);
    setError(undefined);
    try {
      await work();
      setDone(after);
      setMode(undefined);
    } catch (e) {
      setError(((e as { body?: { message?: string } }).body?.message) ?? (e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid gap-3 text-sm">
      <ErrorNote error={detail.error ?? error} />
      {!d && !detail.error ? <p className="m-0 text-muted">Reading what it says…</p> : null}
      {d && (
        <>
          <Markdown text={d.item.summary} fallback={<p>It says nothing more than its name.</p>} />
          <div className="text-xs text-muted">
            {d.item.sources.map((s) => (s.source.startsWith('vault:') ? 'Written back from a session' : `From ${s.rootId ? `${s.rootId}: ` : ''}${s.source}${s.commit ? ` @ ${s.commit.slice(0, 8)}` : ''}`)).join(' · ')}
            {item.label.provenance.principal ? (
              <>
                {' · for '}
                <SubjectName id={item.label.provenance.principal} fallback={item.label.provenance.principal} />
                {item.label.provenance.actor ? `, by ${item.label.provenance.actor}` : ''}
              </>
            ) : null}
          </div>
          {d.neighbours.length > 0 && (
            <div>
              <div className="text-xs text-muted">Connected to</div>
              <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
                {d.neighbours.slice(0, 12).map((n) => (
                  <li key={`${n.item.id}/${n.relation}/${n.direction}`}>
                    <span className="text-muted">{n.direction === 'out' ? n.relation : `${n.relation} by`}</span> {n.item.name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      {done ? (
        <StatusMark state="connected" label={done} />
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="secondary" isDisabled={busy} onPress={() => setMode(mode === 'label' ? undefined : 'label')}>
            Change label
          </Button>
          {owner ? (
            <RowMenu
              label={`More for ${nameOf(item)}`}
              items={[
                { id: 'share', label: 'Share with the organisation', disabled: busy },
                { id: 'remove', label: 'Remove for good…', danger: true, disabled: busy },
              ]}
              onAction={(id) => (id === 'share' ? void run(() => routes.promoteMemory(projectId, { itemIds: [item.id] }), 'Shared with the organisation; it follows this item from now on') : setMode(mode === 'remove' ? undefined : 'remove'))}
            />
          ) : null}
        </div>
      )}
      {mode === 'label' && <Relabel projectId={projectId} item={item} busy={busy} onSave={(label, reason) => void run(() => routes.relabelMemory(projectId, item.id, { label, reason }), 'Label changed').then(onChange)} />}
      {mode === 'remove' && <Purge busy={busy} onPurge={(reason) => void run(() => routes.purgeMemory(projectId, item.id, reason), 'Removed for good').then(onChange)} />}
    </div>
  );
}

function Relabel({ projectId, item, busy, onSave }: { projectId: string; item: MemoryItem; busy: boolean; onSave: (label: { sensitivity: Sensitivity; compartments: string[] }, reason: string) => void }) {
  const compartments = useLoad(() => routes.memoryCompartments(projectId), [projectId]);
  const [sensitivity, setSensitivity] = useState<Sensitivity>(item.label.sensitivity);
  const [held, setHeld] = useState<string[]>(item.label.compartments);
  const [reason, setReason] = useState('');
  const lowering = SENSITIVITIES.indexOf(sensitivity) < SENSITIVITIES.indexOf(item.label.sensitivity);
  const choices = [...new Map([...item.label.compartments.map((id) => ({ id, name: id })), ...(compartments.data ?? [])].map((c) => [c.id, c])).values()];
  return (
    <div className="grid gap-2 border-t border-separator pt-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-muted">Level</span>
        <ChoiceSelect label="Level" value={sensitivity} options={SENSITIVITY_TEXT} onChange={setSensitivity} />
        {choices.map((c) => (
          <Tick key={c.id} checked={held.includes(c.id)} onChange={(on) => setHeld(on ? [...held, c.id] : held.filter((x) => x !== c.id))}>
            {c.name}
          </Tick>
        ))}
      </div>
      <input className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why (kept in the audit trail)" aria-label="Reason" />
      {lowering && <p className="text-xs text-warning">Lowering a label lets more people read it. The audit trail marks it as lowered. A copy already shared with the organisation keeps the higher label.</p>}
      <div>
        <Button size="sm" isDisabled={busy || !reason.trim()} onPress={() => onSave({ sensitivity, compartments: held }, reason.trim())}>
          Save label
        </Button>
      </div>
    </div>
  );
}

function Purge({ busy, onPurge }: { busy: boolean; onPurge: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <div className="grid gap-2 border-t border-separator pt-3">
      <p className="m-0 text-xs">
        <span className="font-medium text-danger">Remove for good. </span>It is deleted, not hidden, together with any copy shared with the organisation. Use it for what must not be kept: a password, a key, personal information.
      </p>
      <input className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why (kept in the audit trail)" aria-label="Reason" />
      <div>
        <Button size="sm" variant="danger-soft" isDisabled={busy || !reason.trim()} onPress={() => onPurge(reason.trim())}>
          Remove for good
        </Button>
      </div>
    </div>
  );
}

/** Owners: whether the project's memory goes into the organisation's as it changes. */
export function ShareMemory({ projectId, mirror, onChange }: { projectId: string; mirror?: { enabled: boolean; scope: string }; onChange: () => void }) {
  const [error, setError] = useState<string>();
  const [note, setNote] = useState<string>();
  const value = !mirror?.enabled ? 'off' : mirror.scope === 'all' ? 'all' : 'knowledge';
  const set = async (v: string) => {
    setError(undefined);
    try {
      const out = await routes.setMemoryMirror(projectId, v === 'off' ? { enabled: false } : { enabled: true, scope: v as 'knowledge' | 'all' });
      setNote(out.catchUp ? `${out.catchUp.copied} of ${out.catchUp.considered} items copied across${out.catchUp.complete ? '.' : '; the rest follow within minutes.'}` : undefined);
      onChange();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const promoteAll = async () => {
    setError(undefined);
    try {
      const out = await routes.promoteMemory(projectId, { all: true });
      setNote(`${out.promoted} items shared. Each follows its source from now on.`);
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <ListGroup title="Share with the organisation" description="What the organisation’s memory holds from this project. A shared copy never has a lower label than the project’s.">
      <ListRow
        title="Keep sharing as it changes"
        description="The organisation’s memory follows the project’s from now on: nothing, its notes and decisions, or everything."
        control={
          <Segment aria-label="Keep sharing as it changes" size="sm" selectedKey={value} onSelectionChange={(k) => void set(String(k))} className="w-fit">
            <Segment.Item id="off">Off</Segment.Item>
            <Segment.Item id="knowledge">Notes and decisions</Segment.Item>
            <Segment.Item id="all">Everything</Segment.Item>
          </Segment>
        }
      />
      <ListRow
        title="Share what you can read now, once"
        description="Each shared item follows its source from then on."
        status={note ? <StatusMark state="connected" label={note} wrap /> : undefined}
        control={
          <Button size="sm" variant="secondary" onPress={() => void promoteAll()}>
            Share now
          </Button>
        }
      />
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}

/** The labelling rules as rows: what a rule matches, and what it adds. */
export function LabellingRulesForm({ projectId, owner }: { projectId: string; owner: boolean }) {
  const rules = useLoad(() => routes.labellingRules(projectId), [projectId]);
  const compartments = useLoad(() => routes.memoryCompartments(projectId), [projectId]);
  const [draft, setDraft] = useState<LabellingRule[]>();
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);
  const rows = draft ?? rules.data ?? [];
  const change = (i: number, patch: Partial<LabellingRule>) => (setSaved(false), setDraft(rows.map((r, j) => (j === i ? ({ ...r, ...patch } as LabellingRule) : r))));
  const save = async () => {
    setError(undefined);
    try {
      await routes.setLabellingRules(projectId, rows);
      setDraft(undefined);
      setSaved(true);
      rules.reload();
    } catch (e) {
      const body = (e as { body?: { issues?: Array<{ message: string; path: unknown[] }> } }).body;
      setError(body?.issues?.[0] ? `Rule ${Number(body.issues[0].path?.[1] ?? 0) + 1}: ${body.issues[0].message}` : (e as Error).message);
    }
  };
  const names = compartments.data ?? [];
  return (
    <ListGroup
      title="Labelling rules"
      count={rows.length || undefined}
      description="The project's machines label what they send with these, top to bottom."
      actions={owner ? <Button size="sm" variant="secondary" onPress={() => (setSaved(false), setDraft([...rows, { id: `rule-${rows.length + 1}`, match: { pathGlob: 'src/**' }, compartments: [], sensitivity: 'internal' }]))}>Add a rule</Button> : undefined}
    >
      {rows.length === 0 ? (
        <ListBlock className="text-sm text-muted">No rules of the project's own. {owner ? 'Add one to label a folder or a source, for example everything under src/ as Internal.' : ''}</ListBlock>
      ) : (
          rows.map((r, i) => {
            const byPath = r.match.pathGlob !== undefined;
            const pattern = (byPath ? r.match.pathGlob : r.match.source) ?? '';
            return (
              <ListRow
                key={i}
                title={`Rule ${i + 1}`}
                description={`${byPath ? 'Files matching' : 'From the source'} ${pattern || '…'} · ${r.sensitivity ? SENSITIVITY_TEXT[r.sensitivity] : 'level left as it is'}${r.compartments.length ? ` · adds ${r.compartments.join(', ')}` : ''}`}
                compactControl
                control={owner ? <RowMenu label={`More for rule ${i + 1}`} items={[{ id: 'remove', label: 'Remove rule', danger: true }]} onAction={() => (setSaved(false), setDraft(rows.filter((_, j) => j !== i)))} /> : undefined}
              >
                <div className="grid gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <ChoiceSelect label="Match by" isDisabled={!owner} value={byPath ? 'path' : 'source'} options={{ path: 'Files matching', source: 'From the source' }} onChange={(v) => change(i, { match: v === 'path' ? { pathGlob: r.match.source ?? 'src/**' } : { source: r.match.pathGlob ?? 'handbook' } })} />
                  <input className={`${inputClass} mono min-w-40 flex-1`} value={(byPath ? r.match.pathGlob : r.match.source) ?? ''} readOnly={!owner} onChange={(e) => change(i, { match: byPath ? { pathGlob: e.target.value } : { source: e.target.value } })} aria-label="Pattern" placeholder={byPath ? 'customers/**' : 'handbook'} />
                  <ChoiceSelect label="Level" isDisabled={!owner} value={r.sensitivity ?? 'leave'} options={{ leave: 'Leave the level', ...SENSITIVITY_TEXT }} onChange={(v) => change(i, { sensitivity: v === 'leave' ? undefined : (v as Sensitivity) })} />
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                  <span className="text-xs text-muted">Add compartments</span>
                  {[...new Map([...r.compartments.map((id) => ({ id, name: id })), ...names].map((c) => [c.id, c])).values()].map((c) => (
                    <Tick key={c.id} isDisabled={!owner} checked={r.compartments.includes(c.id)} onChange={(on) => change(i, { compartments: on ? [...r.compartments, c.id] : r.compartments.filter((x) => x !== c.id) })}>
                      {c.name}
                    </Tick>
                  ))}
                  {names.length === 0 && r.compartments.length === 0 && <span className="text-xs text-muted">An administrator creates compartments on the Memory page.</span>}
                </div>
                </div>
              </ListRow>
            );
          })
      )}
      <ListRow title="Anything else is Confidential" description="This last rule always applies: people cleared to Internal read only what a rule above labels lower." />
      {(owner && draft) || error || rules.error || saved ? (
        <ListBlock className="flex flex-wrap items-center justify-end gap-3">
          {saved && <StatusMark state="connected" label="Saved; machines apply them from their next push" wrap />}
          <ErrorNote error={error ?? rules.error} />
          {owner && draft && (
            <>
              <Button size="sm" variant="ghost" onPress={() => (setDraft(undefined), setError(undefined))}>
                Discard
              </Button>
              <Button size="sm" variant="primary" onPress={() => void save()}>
                Save rules
              </Button>
            </>
          )}
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}
